/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { QuestionnaireType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface QuestionnaireProps {
  id: string;
  name: string;
  companyId: string;
  type: QuestionnaireType;
  communicationCenterId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class QuestionnaireRecord extends Record<QuestionnaireProps>({
  id: null as any,
  name: null as any,
  companyId: null as any,
  type: null as any,
  communicationCenterId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    companyId: string;
    type: QuestionnaireType;
    communicationCenterId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): QuestionnaireRecord {
    if (json == null) {
      throw new Error('QuestionnaireRecord.fromJS: parameter cannot be null');
    }
    return new QuestionnaireRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'QuestionnaireJson.name'),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'QuestionnaireJson.companyId',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', QuestionnaireType.QualityAssurance),
        'QuestionnaireJson.type',
      ),
      communicationCenterId: json.communicationCenterId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    companyId: string;
    type: QuestionnaireType;
    communicationCenterId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [QuestionnaireRecord, Error[]] {
    if (json == null) {
      throw new Error('QuestionnaireRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new QuestionnaireRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'QuestionnaireJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'QuestionnaireJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', QuestionnaireType.QualityAssurance),
            'QuestionnaireJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterId: tryOrDefault(
        () => json.communicationCenterId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: QuestionnaireProps) {
    super(props);
    if (props == null) {
      throw new Error('QuestionnaireRecord constructor parameter cannot be null');
    }
  }
}

export abstract class QuestionnaireViewModelBase implements QuestionnaireProps {
  protected readonly _props: QuestionnaireProps;
  private _json?: QuestionnaireProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get companyId(): string {
    return this._props.companyId;
  }
  get type(): QuestionnaireType {
    return this._props.type;
  }
  get communicationCenterId(): string | null {
    return this._props.communicationCenterId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isQualityAssurance() {
    return this._props.type === QuestionnaireType.QualityAssurance;
  }

  public isAnyType(...questionnaireType: QuestionnaireType[]) {
    return questionnaireType.includes(this._props.type);
  }

  constructor(props: QuestionnaireProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        companyId: this._props.companyId,
        type: this._props.type,
        communicationCenterId: this._props.communicationCenterId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllQuestionnaireEntities = (state: AppState) =>
  state.entityStores.questionnaire.entities;

export const selectQuestionnaireById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaire.entities.get(id);

export const selectQuestionnaireMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.questionnaire.meta.get(id);

export const selectQuestionnaireAndMetadataById = (id: string) =>
  createSelector(
    selectQuestionnaireById(id),
    selectQuestionnaireMetadataById(id),
    (questionnaire, meta) => ({ questionnaire, meta }),
  );

export function selectQuestionnaireEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllQuestionnaireEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, QuestionnaireRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectQuestionnaireEntitiesByName(name: string) {
  return createSelector(selectAllQuestionnaireEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectQuestionnaireEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllQuestionnaireEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectQuestionnaireEntitiesByType(type: QuestionnaireType) {
  return createSelector(selectAllQuestionnaireEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectQuestionnaireEntitiesByCommunicationCenterId(communicationCenterId: string) {
  return createSelector(selectAllQuestionnaireEntities, (entities) =>
    entities.filter((e) => e.communicationCenterId === communicationCenterId).valueSeq(),
  );
}
//#endregion Selectors
